import type { ApiResult } from '../types';
import type { CivilAdviseWizardDraft } from '../types/civilAdviseCaseRequest';
import type { CivilAdviseStepsResponse } from '../types/civilAdviseApi';
import type { DocumentChecklistItem, LookupItem } from '../mocks/criminalMasterData';
import { getCivilAdviseDocumentChecklist } from '../mocks/civilAdviseMasterData';
import { DEFAULT_CIVIL_ADVISE_DRAFT } from '../layouts/CivilAdviseRequestLayout';
import type { CivilAdviseWizardStep } from '../pages/wizard/civilAdvise/civilAdviseConstants';
import { caseApiRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import * as criminalService from './criminalCaseRequests';
import {
  buildSaveCivilAdvisePayload,
  caseRequestIdFromResponse,
  mapCivilAdviseStepsToDraft,
  referenceNoFromResponse,
  resolveCivilAdviseDraftResumeStep,
} from './civilAdviseCaseMapper';

/** Reuse shared master-data APIs. */
export const getFileCategories = criminalService.getFileCategories;
export const getSubFileCategories = criminalService.getSubFileCategories;
export const getSubCategoryApplicableAreas = criminalService.getSubCategoryApplicableAreas;
export const getProvinces = criminalService.getProvinces;
export const getDistricts = criminalService.getDistricts;
export const getUrgencyLevels = criminalService.getUrgencyLevels;
export const getDeliveryMethods = criminalService.getDeliveryMethods;
export const getCorporateInstitutes = criminalService.getCorporateInstitutes;
export const uploadCaseFile = criminalService.uploadCaseFile;
export const downloadCaseFile = criminalService.downloadCaseFile;
export {
  getCommonCoverNote as getCivilAdviseCoverNote,
  downloadCommonCoverNotePdf as downloadCivilAdviseCoverNotePdf,
} from './coverNoteRequests';

export async function getDocumentChecklist(
  fileCategoryId: string,
  subFileCategoryId: string,
): Promise<ApiResult<DocumentChecklistItem[]>> {
  if (!fileCategoryId) return { success: true, data: [] };
  const api = await criminalService.getDocumentChecklist(fileCategoryId, subFileCategoryId);
  if (api.success && api.data.length > 0) return api;
  return { success: true, data: getCivilAdviseDocumentChecklist(subFileCategoryId) };
}

export type SaveCivilAdviseResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

/** CMS-CRCA-1 — save/update civil advice steps 1–4 */
export async function saveCivilAdviseRequest(
  draft: CivilAdviseWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: CivilAdviseWizardStep;
    allSteps?: boolean;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SaveCivilAdviseResult>> {
  const body = buildSaveCivilAdvisePayload(draft, {
    isDraft: options.isDraft,
    currentStep: options.currentStep,
    allSteps: options.allSteps,
    confirmedCorrect: options.confirmedCorrect,
    isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
  });

  const res = await caseApiRequest<CivilAdviseStepsResponse>('/civil-advice/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || draft.draftRequestId;

  return {
    success: true,
    data: {
      requestId,
      fileReferenceNumber: referenceNoFromResponse(res.data),
    },
  };
}

export type CivilAdviseRequestViewData = {
  draft: CivilAdviseWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step1?: CivilAdviseStepsResponse['step_1'];
  step2?: CivilAdviseStepsResponse['step_2'];
  step3?: CivilAdviseStepsResponse['step_3'];
  step4?: CivilAdviseStepsResponse['step_4'];
};

/** CMS-CRCA-2 — full GET payload (wizard + view) */
export async function getCivilAdviseRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<CivilAdviseRequestViewData | null>> {
  if (!caseRequestId.trim()) return { success: true, data: null };

  const res = await caseApiRequest<CivilAdviseStepsResponse>(
    caseRequestStepsPath(`/civil-advice/steps/${encodeURIComponent(caseRequestId)}`, options),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const draft = mapCivilAdviseStepsToDraft(res.data, {
    ...DEFAULT_CIVIL_ADVISE_DRAFT,
    draftRequestId: caseRequestIdFromResponse(res.data) || caseRequestId,
  });

  const statusName = (res.data.status_name ?? res.data.status_code ?? '').trim();
  const submittedAt = (res.data.created_at ?? res.data.updated_at ?? res.data.updatedAt ?? '').trim();
  const raw = res.data as { completion_remarks?: string };

  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step1: res.data.step_1,
      step2: res.data.step_2,
      step3: res.data.step_3,
      step4: res.data.step_4,
    },
  };
}

/** CMS-CRCA-2 — load saved civil advice steps for continue draft / wizard resume */
export async function getCivilAdviseRequestDetails(
  caseRequestId: string,
): Promise<ApiResult<CivilAdviseWizardDraft | null>> {
  const view = await getCivilAdviseRequestViewData(caseRequestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

/** CMS-CRCA-2 — which wizard step to open when continuing a saved draft */
export async function getCivilAdviseDraftResumeStep(
  caseRequestId: string,
): Promise<ApiResult<{ step: CivilAdviseWizardStep; requestId: string }>> {
  if (!caseRequestId.trim()) return { success: false, message: 'Case request id is missing.' };

  const res = await caseApiRequest<CivilAdviseStepsResponse>(
    `/civil-advice/steps/${encodeURIComponent(caseRequestId)}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || caseRequestId;
  return {
    success: true,
    data: { step: resolveCivilAdviseDraftResumeStep(res.data), requestId },
  };
}

export async function submitCivilAdviseRequest(
  draft: CivilAdviseWizardDraft,
): Promise<ApiResult<SaveCivilAdviseResult>> {
  return saveCivilAdviseRequest(draft, {
    isDraft: false,
    allSteps: true,
    confirmedCorrect: draft.confirmCorrect,
  });
}

export function createEmptyCivilAdviseDraft(): CivilAdviseWizardDraft {
  return { ...DEFAULT_CIVIL_ADVISE_DRAFT };
}
